import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { CircularProgress, Stack } from '@mui/material';
import { useGetCategoriesResourcesQuery } from '@/store/apiSlice';
import MainPage from '@/pages/MainPage';
import LoginPage from '@/pages/LoginPage';
import UnauthorizedPage from '@/pages/UnauthorizedPage';
import { UserInfo } from '@/types';

// Admin chunk — only downloaded when the user navigates to an /admin route
const AdminPage = lazy(() => import('@/pages/AdminPage'));
const AdminCategoriesPage = lazy(() => import('@/pages/admin/AdminCategoriesPage'));
const AdminEventsPage = lazy(() => import('@/pages/admin/AdminEventsPage'));
const AdminLogsPage = lazy(() => import('@/pages/admin/AdminLogsPage'));
const AdminMetricsPage = lazy(() => import('@/pages/admin/AdminMetricsPage'));
const MigrationsPage = lazy(() => import('@/pages/MigrationsPage'));

// Install chunk — only downloaded on the /install route
const InstallPage = lazy(() => import('@/pages/InstallPage'));

const Spinner = () => (
    <Stack sx={{ justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
    </Stack>
);

const Router = (props: { admin: UserInfo['admin']; login?: false }) => {
    const { admin } = props;

    const { isLoading: categoryResourcesLoading } = useGetCategoriesResourcesQuery();

    if (categoryResourcesLoading) {
        return <Spinner />;
    }

    return (
        <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            <Route path="/" element={<MainPage />} />

            {/* Install section — separate lazy chunk */}
            <Route
                path="/install"
                element={
                    <Suspense fallback={<Spinner />}>
                        <InstallPage />
                    </Suspense>
                }
            />

            {/* Admin section — separate lazy chunk, admin-only */}
            <Route
                path="/admin"
                element={
                    admin ? (
                        <Suspense fallback={<Spinner />}>
                            <AdminPage />
                        </Suspense>
                    ) : (
                        <Navigate to="/" replace />
                    )
                }
            />
            <Route
                path="/admin/categories"
                element={
                    admin ? (
                        <Suspense fallback={<Spinner />}>
                            <AdminCategoriesPage />
                        </Suspense>
                    ) : (
                        <Navigate to="/" replace />
                    )
                }
            />
            <Route
                path="/admin/events"
                element={
                    admin ? (
                        <Suspense fallback={<Spinner />}>
                            <AdminEventsPage />
                        </Suspense>
                    ) : (
                        <Navigate to="/" replace />
                    )
                }
            />
            <Route
                path="/admin/logs"
                element={
                    admin ? (
                        <Suspense fallback={<Spinner />}>
                            <AdminLogsPage />
                        </Suspense>
                    ) : (
                        <Navigate to="/" replace />
                    )
                }
            />
            <Route
                path="/admin/metrics"
                element={
                    admin ? (
                        <Suspense fallback={<Spinner />}>
                            <AdminMetricsPage />
                        </Suspense>
                    ) : (
                        <Navigate to="/" replace />
                    )
                }
            />
            <Route
                path="/admin/migrations"
                element={
                    admin ? (
                        <Suspense fallback={<Spinner />}>
                            <MigrationsPage />
                        </Suspense>
                    ) : (
                        <Navigate to="/" replace />
                    )
                }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
};

export default Router;
